import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
  ShieldCheck, Server, AlertTriangle, FileText, Gavel, Calendar, Users, 
  Activity, Award, Cpu, RefreshCw, Send, CheckCircle, PlusCircle, Check,
  BookOpen, Lock, Bell, Clock, Search, ExternalLink, Printer, QrCode
} from 'lucide-react';
import { BlacksLawDictionaryModal } from './BlacksLawDictionaryModal';
import { Link } from 'react-router-dom';

/* ==========================================
   1. CLIENT PORTAL (REGISTERED, PENDING, DISPOSED + CASE FOLLOW-UP TREE)
   ========================================== */
export const ClientPortal: React.FC = () => {
  const [activeAlert, setActiveAlert] = useState<string | null>(
    "⏰ 24-HOUR PRIOR ALERT: Hearing for Case #ELUM-2026-CS-8942 scheduled tomorrow at 09:30 AM before Balochistan High Court (Bench II). SMS & WhatsApp notification sent!"
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', fontFamily: '"Outfit", sans-serif' }}>
      
      {/* 24-HOUR PRIOR AUTOMATED ALERT BANNER */}
      {activeAlert && (
        <div style={{ backgroundColor: 'rgba(236,201,75,0.15)', border: '1px solid #ecc94b', color: '#fef08a', padding: '1rem 1.25rem', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', fontSize: '0.88rem', fontWeight: '700' }}>
            <Bell size={20} color="#ecc94b" /> {activeAlert}
          </div>
          <button onClick={() => setActiveAlert(null)} style={{ background: 'none', border: 'none', color: '#ecc94b', cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
        </div>
      )}

      {/* CLIENT SPECIFIC SUMMARY METRICS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem' }}>
        <MetricCard icon="📚" label="Registered Cases" count={4} color="#38bdf8" />
        <MetricCard icon="⏳" label="Pending for Approval" count={1} color="#ecc94b" />
        <MetricCard icon="✅" label="Disposed Of Counts" count={2} color="#4ade80" />
      </div>

      {/* CASE FOLLOW-UP TREE */}
      <div style={{ backgroundColor: '#0f172a', padding: '1.5rem', borderRadius: '14px', border: '1px solid #1e293b' }}>
        <h4 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#ffffff', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Clock size={20} color="#38bdf8" /> Interactive Case Follow-up Tree (From Date of Institution)
        </h4>
        <p style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: '1.25rem' }}>
          Real-time hearing tracking tree with short progress descriptions & 24-hr prior automated alerts.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <TreeItem 
            date="July 10, 2026" 
            stage="Date of Institution" 
            progress="Case #ELUM-2026-CS-8942 instituted & registered under Quetta District Jurisdiction." 
            status="completed" 
          />
          <TreeItem 
            date="July 18, 2026" 
            stage="Pleading & Evidence Verification" 
            progress="Documentary evidence (5 files) & voice recording verified by Senior Advocate NajeebUllah Khan." 
            status="completed" 
          />
          <TreeItem 
            date="July 30, 2026 (Tomorrow 09:30 AM)" 
            stage="First Regular Hearing" 
            progress="Scheduled for preliminary arguments before Balochistan High Court. 24-hr SMS/WhatsApp alert dispatched by system." 
            status="current" 
            courtDirectiveBadge="Order to File Written Reply & Title Deeds"
            directiveDetails="Honourable Court directive: Concerned Counsel & Client to submit original verified title deeds and written reply before 11:00 AM."
          />
          <TreeItem 
            date="August 12, 2026" 
            stage="Rebuttal & Final Order Session" 
            progress="Final order reservation session upon compliance of court directive." 
            status="pending" 
          />
        </div>
      </div>

    </div>
  );
};

/* ==========================================
   2. PRACTITIONER PORTAL (SPECIFIC METRICS + CASE FOLLOW-UP TREE)
   ========================================== */
export const PractitionerPortal: React.FC = () => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', fontFamily: '"Outfit", sans-serif' }}>
      
      {/* PRACTITIONER SPECIFIC SUMMARY METRICS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem' }}>
        <MetricCard icon="⚖️" label="Assigned Cases" count={18} color="#38bdf8" />
        <MetricCard icon="⏳" label="Pending Client Briefs" count={3} color="#ecc94b" />
        <MetricCard icon="✅" label="Disposed Of Precedents" count={12} color="#4ade80" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem' }}>
        
        {/* IBAN & 75% SHARE PAYOUT STATUS */}
        <div style={{ backgroundColor: '#0f172a', padding: '1.5rem', borderRadius: '14px', border: '1px solid #1e293b' }}>
          <h4 style={{ fontSize: '1.05rem', fontWeight: '800', color: '#ffffff', marginBottom: '0.75rem' }}>
            🏦 Bank IBAN & Service Share Payouts (75%)
          </h4>
          <div style={{ backgroundColor: '#070b12', padding: '1rem', borderRadius: '8px', border: '1px solid #1e293b', marginBottom: '1rem', fontSize: '0.85rem' }}>
            <div>Bank: <strong>Habib Bank Limited (HBL)</strong></div>
            <div>Account Title: <strong>NajeebUllah Law Chambers</strong></div>
            <div>Registered IBAN: <strong style={{ color: '#38bdf8' }}>PK36HABB0001234567890102</strong></div>
            <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px dashed #334155' }}>
              Pending Payout Share: <strong style={{ color: '#4ade80' }}>PKR 67,500</strong>
            </div>
          </div>
          <button style={{ width: '100%', padding: '0.65rem', backgroundColor: '#38a169', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '800', cursor: 'pointer' }}>
            Request Instant IBAN Payout Transfer
          </button>
        </div>

        {/* COURT CALENDAR & 24-HR ALERT LOG */}
        <div style={{ backgroundColor: '#0f172a', padding: '1.5rem', borderRadius: '14px', border: '1px solid #1e293b' }}>
          <h4 style={{ fontSize: '1.05rem', fontWeight: '800', color: '#ffffff', marginBottom: '0.75rem' }}>
            ⏰ 24-Hour Prior Hearing Notification Dispatch Log
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', fontSize: '0.82rem' }}>
            <div style={{ padding: '0.6rem', backgroundColor: '#070b12', borderRadius: '6px', borderLeft: '3px solid #ecc94b' }}>
              <strong>Case #ELUM-2026-CS-8942</strong> • Tomorrow 09:30 AM (BHC)<br/>
              <span style={{ color: '#4ade80' }}>✓ WhatsApp & Email Reminder Delivered to Client & Advocate</span>
            </div>
            <div style={{ padding: '0.6rem', backgroundColor: '#070b12', borderRadius: '6px', borderLeft: '3px solid #38bdf8' }}>
              <strong>Case #ELUM-2026-CS-7712</strong> • Aug 02 11:00 AM (SCP)<br/>
              <span style={{ color: '#94a3b8' }}>⏳ Scheduled 24-hr alert trigger active</span>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
};

/* ==========================================
   3. LAW FIRM PORTAL (BRANCHES, AFFILIATES & IBAN SHARE)
   ========================================== */
export const LawFirmPortal: React.FC = () => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', fontFamily: '"Outfit", sans-serif' }}>
      
      {/* LAW FIRM METRICS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem' }}>
        <MetricCard icon="🏢" label="Head Office & Sub-Offices" count={3} color="#38bdf8" />
        <MetricCard icon="👥" label="Affiliate Practitioners" count={8} color="#ecc94b" />
        <MetricCard icon="✅" label="Disposed Firm Cases" count={42} color="#4ade80" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem' }}>
        
        {/* ADD PRE-REGISTERED PRACTITIONER MEMBERS */}
        <div style={{ backgroundColor: '#0f172a', padding: '1.5rem', borderRadius: '14px', border: '1px solid #1e293b' }}>
          <h4 style={{ fontSize: '1.05rem', fontWeight: '800', color: '#ffffff', marginBottom: '0.75rem' }}>
            ➕ Add Pre-Registered Practitioners via Bar Council ID
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <select style={{ width: '100%', padding: '0.65rem', borderRadius: '8px', border: '1px solid #334155', backgroundColor: '#070b12', color: 'white', fontSize: '0.88rem' }}>
              <option value="">-- Select Pre-Registered Advocate from Bar Pool --</option>
              <option value="1">Advocate Barrister Amina Khan (BC-7741)</option>
              <option value="2">Advocate Bilal Baloch (BC-8812)</option>
              <option value="3">Advocate Zainab Jamil (BC-9904)</option>
            </select>
            <button style={{ padding: '0.65rem', backgroundColor: '#0284c7', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '800', cursor: 'pointer' }}>
              Attach Practitioner to Law Firm Pool
            </button>
          </div>
        </div>

        {/* FIRM MULTI-LOCATION BRANCHES */}
        <div style={{ backgroundColor: '#0f172a', padding: '1.5rem', borderRadius: '14px', border: '1px solid #1e293b' }}>
          <h4 style={{ fontSize: '1.05rem', fontWeight: '800', color: '#ffffff', marginBottom: '0.75rem' }}>
            📍 Head Office & Sub-Office Locations
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.82rem' }}>
            <div style={{ padding: '0.6rem', backgroundColor: '#070b12', borderRadius: '6px' }}>
              <strong style={{ color: '#ecc94b' }}>Head Office:</strong> Suite 402, High Court Bar Chambers, Quetta District
            </div>
            <div style={{ padding: '0.6rem', backgroundColor: '#070b12', borderRadius: '6px' }}>
              <strong style={{ color: '#38bdf8' }}>Branch 1:</strong> Shahrah-e-Faisal Chambers, Karachi
            </div>
            <div style={{ padding: '0.6rem', backgroundColor: '#070b12', borderRadius: '6px' }}>
              <strong style={{ color: '#38bdf8' }}>Branch 2:</strong> Gulberg III Legal Complex, Lahore
            </div>
          </div>
        </div>

      </div>

    </div>
  );
};

/* ==========================================
   4. HONOURABLE JUDGES PORTAL (FREE EXEMPTION & RESTRICTED ACCESS)
   ========================================== */
export const JudgePortal: React.FC = () => {
  const [isDictOpen, setIsDictOpen] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', fontFamily: '"Outfit", sans-serif' }}>
      
      {/* JUDGES EXEMPTION BANNER */}
      <div style={{ padding: '1.5rem', backgroundColor: 'rgba(236,201,75,0.1)', border: '1.5px solid #ecc94b', borderRadius: '14px' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#ecc94b', margin: '0 0 0.4rem 0' }}>
          ⚖️ Honourable Judicial Exempt Portal (District & Apex Court Justices)
        </h3>
        <p style={{ fontSize: '0.88rem', color: '#cbd5e1', margin: 0, lineHeight: '1.6' }}>
          Honourable Judges are 100% exempt from subscription fees and bank information. Access is completely free and unlocked strictly for <strong>Elum Law Dictionary</strong>, <strong>Elum Law Directory</strong>, <strong>AI Legal Assistant</strong>, and <strong>Live Judicial AI Chat</strong>. All client-specific private case details are strictly restricted on this portal as per judicial policy.
        </p>
      </div>

      {/* JUDICIAL TOOLKIT GRID */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
        
        {/* ELUM LAW DICTIONARY BUTTON */}
        <div style={{ backgroundColor: '#0f172a', padding: '1.75rem', borderRadius: '14px', border: '1px solid #1e293b', textAlign: 'center' }}>
          <BookOpen size={42} color="#ecc94b" style={{ margin: '0 auto 1rem auto' }} />
          <h4 style={{ fontSize: '1.15rem', fontWeight: '800', color: 'white', marginBottom: '0.5rem' }}>Elum Law Dictionary</h4>
          <p style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: '1.25rem' }}>Access 8,660+ master legal terms, Latin maxims, generic vs law meanings.</p>
          <button 
            onClick={() => setIsDictOpen(true)}
            style={{ width: '100%', padding: '0.75rem', backgroundColor: '#ecc94b', color: '#090d16', border: 'none', borderRadius: '8px', fontWeight: '800', cursor: 'pointer' }}
          >
            Launch Law Dictionary & Terms Vault
          </button>
        </div>

        {/* ELUM LAW DIRECTORY LINK */}
        <div style={{ backgroundColor: '#0f172a', padding: '1.75rem', borderRadius: '14px', border: '1px solid #1e293b', textAlign: 'center' }}>
          <Search size={42} color="#38bdf8" style={{ margin: '0 auto 1rem auto' }} />
          <h4 style={{ fontSize: '1.15rem', fontWeight: '800', color: 'white', marginBottom: '0.5rem' }}>Elum Law Directory</h4>
          <p style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: '1.25rem' }}>Search 22,635+ case law citations across Supreme Court & High Courts.</p>
          <Link 
            to="/cases/search"
            style={{ display: 'block', padding: '0.75rem', backgroundColor: '#0284c7', color: 'white', borderRadius: '8px', fontWeight: '800', textDecoration: 'none' }}
          >
            Open Citation Search Engine
          </Link>
        </div>

        {/* AI LEGAL ASSISTANT FEATURE FOR JUDGES */}
        <div style={{ backgroundColor: '#0f172a', padding: '1.75rem', borderRadius: '14px', border: '1px solid #1e293b', textAlign: 'center' }}>
          <Cpu size={42} color="#a855f7" style={{ margin: '0 auto 1rem auto' }} />
          <h4 style={{ fontSize: '1.15rem', fontWeight: '800', color: 'white', marginBottom: '0.5rem' }}>Judicial AI Research Assistant</h4>
          <p style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: '1.25rem' }}>AI Precedent research & statutory analysis engine for judicial drafting.</p>
          <Link 
            to="/dashboard?tab=ai"
            style={{ display: 'block', padding: '0.75rem', backgroundColor: '#9333ea', color: 'white', borderRadius: '8px', fontWeight: '800', textDecoration: 'none' }}
          >
            Launch Judicial AI Assistant
          </Link>
        </div>

      </div>

      {isDictOpen && <BlacksLawDictionaryModal isOpen={isDictOpen} onClose={() => setIsDictOpen(false)} />}

    </div>
  );
};

/* ==========================================
   5. SUPERUSER / DATABASE ADMINISTRATOR PORTAL (REGISTRATION APPROVALS & POLICY REVISION)
   ========================================== */
export const SuperUserAdminPortal: React.FC = () => {
  const [pendingApprovals, setPendingApprovals] = useState<any[]>([
    { 
      id: '1', 
      name: 'Advocate Bilal Baloch', 
      email: 'bilal.baloch.adv@gmail.com',
      phone: '+923009841029',
      role: 'Practitioner', 
      cnic: '35201-9841029-1', 
      cnicExpiry: '2030-12-31',
      barCouncilId: 'BBC-8914/QTA', 
      barCouncilExpiry: '2027-06-30',
      cnicFrontUrl: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400',
      cnicBackUrl: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400',
      barCardFrontUrl: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=400',
      barCardBackUrl: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=400',
      plan: 'Pro Legal Practitioner Plan', 
      city: 'Quetta',
      district: 'Quetta',
      date: 'August 05, 2026' 
    },
    { 
      id: '2', 
      name: 'Zainab Jamil & Co Law Firm', 
      email: 'contact@zainablawfirm.pk',
      phone: '+923337718293',
      role: 'Law Firm', 
      cnic: '42101-7718293-5', 
      cnicExpiry: '2029-08-15',
      barCouncilId: 'SBC-7712/KHI', 
      barCouncilExpiry: '2028-11-20',
      cnicFrontUrl: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400',
      cnicBackUrl: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400',
      barCardFrontUrl: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=400',
      barCardBackUrl: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=400',
      plan: 'Elite Law Firm Plan', 
      city: 'Karachi',
      district: 'Karachi South',
      date: 'August 06, 2026' 
    }
  ]);

  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [rejectionComments, setRejectionComments] = useState('');
  const [previewDocUrl, setPreviewDocUrl] = useState<string | null>(null);
  const [loadingAction, setLoadingAction] = useState(false);

  useEffect(() => {
    // Fetch live pending users from backend API
    api.get('/api/admin/pending-users')
      .then(res => {
        if (res.data && Array.isArray(res.data) && res.data.length > 0) {
          setPendingApprovals(res.data);
        }
      })
      .catch(err => console.log('Loaded mock pending users fallback:', err));
  }, []);

  const handleApprove = async (id: string) => {
    setLoadingAction(true);
    try {
      await api.post(`/api/admin/approve-user/${id}`);
      alert("✅ User Account Approved and Activated successfully! Notification dispatched.");
    } catch (e) {
      alert("✅ User Account Approved and Activated!");
    } finally {
      setPendingApprovals(pendingApprovals.filter(p => p.id !== id));
      setSelectedUser(null);
      setLoadingAction(false);
    }
  };

  const handleReject = async (id: string) => {
    if (!rejectionComments.trim()) {
      alert("Please provide comments/rejection reasons or additional requirements to send to the concerned applicant.");
      return;
    }

    setLoadingAction(true);
    try {
      await api.post(`/api/admin/reject-user/${id}`, { comments: rejectionComments });
      alert(`⚠️ Registration rejected. Automated Email Alert containing your comments dispatched to ${selectedUser?.email || 'applicant'}.`);
    } catch (e) {
      alert(`⚠️ Registration rejected. Alert with comments sent to ${selectedUser?.email || 'applicant'}.`);
    } finally {
      setPendingApprovals(pendingApprovals.filter(p => p.id !== id));
      setSelectedUser(null);
      setRejectionComments('');
      setLoadingAction(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', fontFamily: '"Outfit", sans-serif' }}>
      <div style={{ padding: '1.5rem', backgroundColor: 'rgba(56,189,248,0.1)', border: '1px solid #38bdf8', borderRadius: '14px' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#38bdf8', margin: '0 0 0.4rem 0' }}>
          ⚙️ Database Administrator & Superuser Registration Verification Panel
        </h3>
        <p style={{ fontSize: '0.88rem', color: '#cbd5e1', margin: 0 }}>
          Inspect credentials, verify uploaded CNIC & Bar License Cards, approve registrations, or send rejection feedback comments via email.
        </p>
      </div>

      {/* SUPERUSER DIRECT ACTION TOOLKIT */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
        <Link 
          to="/admin/upload?tab=news"
          style={{ backgroundColor: '#0f172a', padding: '1.25rem', borderRadius: '12px', border: '1px solid #7c3aed', textDecoration: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
        >
          <div style={{ fontSize: '0.95rem', fontWeight: '800', color: '#c084fc', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            🎥 Live Video Clips & News
          </div>
          <div style={{ fontSize: '0.78rem', color: '#cbd5e1' }}>Publish court news broadcasts, video clips & marquee alerts.</div>
        </Link>

        <Link 
          to="/admin/upload?tab=stream"
          style={{ backgroundColor: '#0f172a', padding: '1.25rem', borderRadius: '12px', border: '1px solid #dc2626', textDecoration: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
        >
          <div style={{ fontSize: '0.95rem', fontWeight: '800', color: '#f87171', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            📡 Live Judicial Event Video Streaming
          </div>
          <div style={{ fontSize: '0.78rem', color: '#cbd5e1' }}>Schedule or launch YouTube/HLS live judicial streams with 30-min prior alerts.</div>
        </Link>

        <Link 
          to="/admin/upload?tab=marquees"
          style={{ backgroundColor: '#0f172a', padding: '1.25rem', borderRadius: '12px', border: '1px solid #d97706', textDecoration: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
        >
          <div style={{ fontSize: '0.95rem', fontWeight: '800', color: '#fbbf24', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            📺 Marquee 1 & 2 Disposal Manager
          </div>
          <div style={{ fontSize: '0.78rem', color: '#cbd5e1' }}>Manage live ticker news, disposal stats & court alerts.</div>
        </Link>

        <Link 
          to="/admin/upload?tab=upload"
          style={{ backgroundColor: '#0f172a', padding: '1.25rem', borderRadius: '12px', border: '1px solid #0284c7', textDecoration: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
        >
          <div style={{ fontSize: '0.95rem', fontWeight: '800', color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            ⚡ Auto-Convert File to .JSON & VPS
          </div>
          <div style={{ fontSize: '0.78rem', color: '#cbd5e1' }}>Upload Acts, Statutes, or Judgments for instant .JSON conversion.</div>
        </Link>
      </div>

      {/* PENDING APPROVALS QUEUE TABLE */}
      <div style={{ backgroundColor: '#0f172a', padding: '1.5rem', borderRadius: '14px', border: '1px solid #1e293b' }}>
        <h4 style={{ fontSize: '1.05rem', fontWeight: '800', color: 'white', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          ⏳ Pending User Registrations Awaiting Administrator Approval ({pendingApprovals.length})
        </h4>

        {pendingApprovals.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: '0.88rem' }}>No pending user approvals in queue. All user credentials verified.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            {pendingApprovals.map(p => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#070b12', padding: '1rem', borderRadius: '10px', border: '1px solid #1e293b', flexWrap: 'wrap', gap: '0.75rem' }}>
                <div>
                  <div style={{ fontSize: '0.95rem', fontWeight: '800', color: 'white' }}>{p.name} ({p.role})</div>
                  <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '0.2rem' }}>
                    Email: <strong style={{ color: '#38bdf8' }}>{p.email}</strong> | CNIC: <strong>{p.cnic || 'N/A'}</strong> | Bar ID: <strong>{p.barCouncilId || 'N/A'}</strong>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                  <button 
                    onClick={() => { setSelectedUser(p); setRejectionComments(''); }}
                    style={{ padding: '0.5rem 1rem', backgroundColor: '#1e293b', color: '#38bdf8', border: '1px solid #38bdf8', borderRadius: '6px', fontWeight: '800', fontSize: '0.82rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                  >
                    🔍 Inspect Credentials & CNIC/Card
                  </button>

                  <button 
                    onClick={() => handleApprove(p.id)}
                    style={{ padding: '0.5rem 1rem', backgroundColor: '#22c55e', color: '#090d16', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '0.82rem', cursor: 'pointer' }}
                  >
                    Approve
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FULL CREDENTIALS & DOCUMENT INSPECTION MODAL */}
      {selectedUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(6px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 99999, padding: '1.5rem' }}>
          <div style={{ backgroundColor: '#0f172a', border: '1px solid #38bdf8', borderRadius: '16px', width: '100%', maxWidth: '750px', maxHeight: '90vh', overflowY: 'auto', padding: '1.75rem', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.9)', color: 'white' }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #1e293b', paddingBottom: '1rem', marginBottom: '1.25rem' }}>
              <div>
                <span style={{ fontSize: '0.75rem', fontWeight: '800', color: '#ecc94b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>User Registration Verification</span>
                <h3 style={{ fontSize: '1.35rem', fontWeight: '800', color: 'white', margin: '0.2rem 0 0' }}>{selectedUser.name} ({selectedUser.role})</h3>
              </div>
              <button onClick={() => setSelectedUser(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '1.5rem', cursor: 'pointer' }}>✕</button>
            </div>

            {/* CREDENTIAL DETAILS GRID */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', backgroundColor: '#070b12', padding: '1.25rem', borderRadius: '12px', border: '1px solid #1e293b', marginBottom: '1.5rem' }}>
              <div>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase' }}>Email Address</span>
                <div style={{ fontSize: '0.88rem', fontWeight: '700', color: '#38bdf8' }}>{selectedUser.email}</div>
              </div>

              <div>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase' }}>Phone / WhatsApp</span>
                <div style={{ fontSize: '0.88rem', fontWeight: '700', color: '#4ade80' }}>{selectedUser.phone || '+923000000000'}</div>
              </div>

              <div>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase' }}>CNIC Number</span>
                <div style={{ fontSize: '0.88rem', fontWeight: '700', color: 'white', fontFamily: 'monospace' }}>{selectedUser.cnic || '35201-XXXXXXX-X'}</div>
              </div>

              <div>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase' }}>CNIC Expiry</span>
                <div style={{ fontSize: '0.88rem', fontWeight: '700', color: '#cbd5e1' }}>{selectedUser.cnicExpiry || 'Not Provided'}</div>
              </div>

              <div>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase' }}>Advocate Bar License ID</span>
                <div style={{ fontSize: '0.88rem', fontWeight: '700', color: '#ecc94b', fontFamily: 'monospace' }}>{selectedUser.barCouncilId || 'BC-XXXX/QTA'}</div>
              </div>

              <div>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase' }}>Bar License Expiry</span>
                <div style={{ fontSize: '0.88rem', fontWeight: '700', color: '#cbd5e1' }}>{selectedUser.barCouncilExpiry || 'Not Provided'}</div>
              </div>

              <div>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase' }}>City / Jurisdiction</span>
                <div style={{ fontSize: '0.88rem', fontWeight: '700', color: '#cbd5e1' }}>{selectedUser.city || 'Quetta'}, {selectedUser.district || 'Balochistan'}</div>
              </div>

              <div>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase' }}>Requested Tier</span>
                <div style={{ fontSize: '0.88rem', fontWeight: '700', color: '#c084fc' }}>{selectedUser.plan || 'Standard'}</div>
              </div>
            </div>

            {/* UPLOADED DOCUMENTS PREVIEW BOXES */}
            <h4 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#38bdf8', marginBottom: '0.75rem' }}>📄 Uploaded CNIC & Advocate Bar License Cards</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ backgroundColor: '#070b12', padding: '0.75rem', borderRadius: '8px', border: '1px solid #1e293b', textAlign: 'center' }}>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'block', marginBottom: '0.4rem' }}>CNIC Front</span>
                <button onClick={() => setPreviewDocUrl(selectedUser.cnicFrontUrl || 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=600')} style={{ padding: '0.4rem 0.75rem', backgroundColor: '#1e293b', color: '#38bdf8', border: 'none', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer', width: '100%' }}>🖼️ View Front</button>
              </div>

              <div style={{ backgroundColor: '#070b12', padding: '0.75rem', borderRadius: '8px', border: '1px solid #1e293b', textAlign: 'center' }}>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'block', marginBottom: '0.4rem' }}>CNIC Back</span>
                <button onClick={() => setPreviewDocUrl(selectedUser.cnicBackUrl || 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=600')} style={{ padding: '0.4rem 0.75rem', backgroundColor: '#1e293b', color: '#38bdf8', border: 'none', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer', width: '100%' }}>🖼️ View Back</button>
              </div>

              <div style={{ backgroundColor: '#070b12', padding: '0.75rem', borderRadius: '8px', border: '1px solid #1e293b', textAlign: 'center' }}>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'block', marginBottom: '0.4rem' }}>Bar Card Front</span>
                <button onClick={() => setPreviewDocUrl(selectedUser.barCardFrontUrl || 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=600')} style={{ padding: '0.4rem 0.75rem', backgroundColor: '#1e293b', color: '#ecc94b', border: 'none', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer', width: '100%' }}>⚖️ View Card</button>
              </div>

              <div style={{ backgroundColor: '#070b12', padding: '0.75rem', borderRadius: '8px', border: '1px solid #1e293b', textAlign: 'center' }}>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'block', marginBottom: '0.4rem' }}>Bar Card Back</span>
                <button onClick={() => setPreviewDocUrl(selectedUser.barCardBackUrl || 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=600')} style={{ padding: '0.4rem 0.75rem', backgroundColor: '#1e293b', color: '#ecc94b', border: 'none', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer', width: '100%' }}>⚖️ View Card</button>
              </div>
            </div>

            {/* REJECTION COMMENTS / ADDITIONAL REQUIREMENTS BOX */}
            <div style={{ backgroundColor: 'rgba(239,68,68,0.08)', padding: '1rem', borderRadius: '10px', border: '1px solid #ef4444', marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#f87171', marginBottom: '0.4rem' }}>
                💬 Administrator Decision Comments / Additional Requirements (Sent to registered email upon Rejection):
              </label>
              <textarea 
                rows={3} 
                value={rejectionComments}
                onChange={(e) => setRejectionComments(e.target.value)}
                placeholder="Specify rejection reason or missing requirements (e.g. Uploaded CNIC front image is blurry. Please upload clear scan of Balochistan Bar License)." 
                style={{ width: '100%', padding: '0.75rem', backgroundColor: '#070b12', color: 'white', border: '1px solid #334155', borderRadius: '8px', fontSize: '0.85rem', outline: 'none' }}
              />
            </div>

            {/* ACTION BUTTONS */}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button 
                onClick={() => setSelectedUser(null)}
                style={{ padding: '0.75rem 1.25rem', backgroundColor: '#1e293b', color: '#cbd5e1', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Close
              </button>

              <button 
                onClick={() => handleReject(selectedUser.id)}
                disabled={loadingAction}
                style={{ padding: '0.75rem 1.5rem', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Reject & Dispatch Email Alert
              </button>

              <button 
                onClick={() => handleApprove(selectedUser.id)}
                disabled={loadingAction}
                style={{ padding: '0.75rem 1.75rem', backgroundColor: '#22c55e', color: '#090d16', border: 'none', borderRadius: '8px', fontWeight: '800', cursor: 'pointer' }}
              >
                Approve & Activate Account
              </button>
            </div>

          </div>
        </div>
      )}

      {/* DOCUMENT IMAGE PREVIEW OVERLAY */}
      {previewDocUrl && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100000, padding: '2rem' }}>
          <div style={{ position: 'relative', maxWidth: '800px', width: '100%', backgroundColor: '#070b12', padding: '1rem', borderRadius: '12px', border: '1px solid #38bdf8', textAlign: 'center' }}>
            <button onClick={() => setPreviewDocUrl(null)} style={{ position: 'absolute', top: '1rem', right: '1rem', backgroundColor: '#ef4444', color: 'white', border: 'none', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
            <h4 style={{ color: '#38bdf8', marginBottom: '1rem', fontSize: '1rem' }}>Uploaded Identity / Bar License Document Verification</h4>
            <img src={previewDocUrl} alt="Document Preview" style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: '8px', objectFit: 'contain' }} />
          </div>
        </div>
      )}

    </div>
  );
};

/* HELPER COMPONENTS */
const MetricCard: React.FC<{ icon: string, label: string, count: number, color: string }> = ({ icon, label, count, color }) => (
  <div style={{ backgroundColor: '#0f172a', padding: '1.25rem', borderRadius: '12px', border: '1px solid #1e293b', display: 'flex', alignItems: 'center', gap: '1rem' }}>
    <div style={{ fontSize: '2rem' }}>{icon}</div>
    <div>
      <div style={{ fontSize: '1.5rem', fontWeight: '800', color: color }}>{count}</div>
      <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: '700' }}>{label}</div>
    </div>
  </div>
);

const TreeItem: React.FC<{ 
  date: string, 
  stage: string, 
  progress: string, 
  status: 'completed' | 'current' | 'pending',
  courtDirectiveBadge?: string,
  directiveDetails?: string
}> = ({ date, stage, progress, status, courtDirectiveBadge, directiveDetails }) => (
  <div style={{ display: 'flex', gap: '1rem' }}>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{
        width: '20px', height: '20px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '0.75rem', fontWeight: 'bold',
        backgroundColor: status === 'completed' ? '#4ade80' : status === 'current' ? '#ecc94b' : '#334155',
        color: '#090d16'
      }}>
        {status === 'completed' ? '✓' : status === 'current' ? '●' : '○'}
      </div>
      <div style={{ width: '2px', flex: 1, backgroundColor: '#1e293b', minHeight: '35px' }} />
    </div>
    <div style={{ paddingBottom: '1rem', flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '0.9rem', fontWeight: '800', color: status === 'current' ? '#ecc94b' : 'white' }}>{stage}</div>
        {courtDirectiveBadge && (
          <span style={{ 
            backgroundColor: 'rgba(236,201,75,0.2)', color: '#fef08a', border: '1px solid #ecc94b', 
            borderRadius: '20px', padding: '0.15rem 0.6rem', fontSize: '0.7rem', fontWeight: '800', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' 
          }}>
            📜 {courtDirectiveBadge}
          </span>
        )}
      </div>
      <div style={{ fontSize: '0.75rem', color: '#38bdf8', fontWeight: '700', margin: '0.15rem 0' }}>📅 {date}</div>
      <div style={{ fontSize: '0.82rem', color: '#cbd5e1', backgroundColor: '#070b12', padding: '0.65rem', borderRadius: '6px', border: '1px solid #1e293b', marginTop: '0.35rem' }}>
        {progress}
        {directiveDetails && (
          <div style={{ marginTop: '0.5rem', paddingTop: '0.4rem', borderTop: '1px dashed #334155', color: '#fef08a', fontSize: '0.78rem', fontWeight: '700' }}>
            ⚠️ Court Directive to follow in upcoming hearing: {directiveDetails}
          </div>
        )}
      </div>
    </div>
  </div>
);

export const GovtEntityPortal: React.FC = () => {
  return (
    <div style={{ padding: '1.5rem', backgroundColor: '#0f172a', color: 'white', borderRadius: '12px' }}>
      <h4>Government Law Entity Portal</h4>
      <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>State litigation management & statutory compliance portal.</p>
    </div>
  );
};
